'use client';

import { addToCart, specialCartStore, updateCart } from '@/api/cart';
import {
  fetchCoatingsShow,
  fetchLensShow,
  fetchPrescriptionFillMethods,
  fetchProgressiveViewArea,
} from '@/api/product';
import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { IBaseCartData, IBaseCartGoodsItem } from '@/api/types/cart';
import { IPrice } from '@/api/types/public';
import {
  COATING_COMPONENT,
  EDIT_PRESCRIPTION_COMPONENT,
  EDIT_PRESCRIPTION_SUB_STEPS,
  LENS_INDEX_COMPONENT,
  LENS_TYPE_COMPONENT,
  LENS_VIEW_AREA_COMPONENT,
  PRESCRIPTION_TYPE_COMPONENT,
} from '@/app/[variants]/_lens-components/constants';
import ClientLeft from '@/app/[variants]/_lens-components/layout/ClientLeft';
import Footer from '@/app/[variants]/_lens-components/layout/Footer';
import Header from '@/app/[variants]/_lens-components/layout/Header';
import Coating from '@/app/[variants]/_lens-components/steps/Coating';
import LensType from '@/app/[variants]/_lens-components/steps/LensType';
import { useLensProcessStore } from '@/app/[variants]/_lens-components/store/lens-process-store';
import { processClassName } from '@/app/[variants]/_lens-components/style/responsive';
import {
  BaseCartRequestData,
  BasePresTypeList,
  CoatingItem,
  CustomerPrescription,
  ELensViewAreaType,
  IGoodsDetail,
  LensShowResponse,
} from '@/app/[variants]/_lens-components/types';
import {
  getApiPrescriptionType,
  handleCoatingPriceInfo,
  handleColorItemPriceInfo,
  handleGoodsFramePriceInfo,
  handleLensIndexPriceInfo,
  handlerPrescriptionData,
} from '@/app/[variants]/_lens-components/utils';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { getFeatureValueUsedList } from '@/utils/feature-value';
import { add } from '@/utils/math';
import { hasPrism } from '@/utils/prescription';
import { randomString } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import EditPrescription, { EditPrescriptionRef } from './components/steps/EditPrescription';
import ReadersPrescription, {
  ReadersPrescriptionRef,
} from './components/steps/EditPrescription/components/ReadersPrescription';
import LensIndex from './components/steps/LensIndex';
import LensViewArea from './components/steps/LensViewArea';
import PrescriptionType from './components/steps/PrescriptionType';
// import PrescriptionCorrectionDialog from './components/steps/EditPrescription/components/PrescriptionCorrectionDialog';
import { usePrevious } from 'ahooks';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';

export default function ClientPage({
  glassPresTypeList,
  goodsDetail,
  cartItemData,
  isRedeem,
  onClose,
  onUpdateCart,
  inDialog = false,
}: {
  glassPresTypeList?: BasePresTypeList;
  goodsDetail?: IGoodsDetail;
  cartItemData?: IBaseCartGoodsItem;
  isRedeem: boolean;
  onClose?: () => void;
  onUpdateCart?: (data: IBaseCartData) => void;
  inDialog?: boolean;
}) {
  const router = useRouter();
  // 是否是 frame 配镜步骤
  const isFrameProcess = false;
  const { trackEvent } = useReporting();
  const isWap = useMediaQuery('(max-width:1280px)');
  const t = useTranslations('lens-process');
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const currencyReady = useStore((state) => state.currencyReady);

  // 配镜流程状态
  const addToCartText = isRedeem ? t('checkout') : t('addToCart');

  const {
    // 是否是无处方类型（包括无处方、太阳镜无处方）
    isNonPrescriptionType,
    setIsNonPrescriptionType,
    // 商品基本信息
    lensGoodsItem,
    setLensGoodsItem,
    // 神策埋点商品数据（包括一进入配镜流程就确定的product_view_uuid、entrance_edit_lens_scene）
    setLensGoodsSensorsData,
    // 是否是积分兑换商品
    setIsRedeem,
    prescriptionType,
    setPresTypeName,
    setPresTypeOptionName,
    prescription,
    readerSelectedIndex,
    setPrescription,
    scanInfo,
    setScanInfo,
    setPrescriptionFillMethodsData,
    // 价格相关
    hasIndexPrice,
    setHasIndexPrice,
    // 以下变量在组件中使用，但在埋点方法中也会用到
    setGoodsFramePriceInfo,
    setTotalPrice,
    setLensTypePriceInfo,
    setLensIndexPriceInfo,
    setCoatingPriceInfo,
    // 选中项
    selectedColorItem,
    setSelectedColorItem,
    selectedAdvancedLensIndexItem, // 注意：视野区域步骤仅修改这项，不会更新 selectedLensIndexItem
    setSelectedAdvancedLensIndexItem,
    selectedLensIndexItem,
    setSelectedLensIndexItem,
    selectedCoating,
    setSelectedCoating,
    selectedLensType,
    setSelectedLensType,
    selectedBrand,
    setSelectedBrand,
    // 埋点方法
    SDLensTypePageNextClick,
    SDLensIndexPageNextClick,
    SDSendLeaveEvent,
    SDEditLensPageView,
    SDEditLensPageClose,
    SDAddToCartBtnClick,
    SDAddToCartResult,
    sendPoints,
    setTtqEventId,
    setCampaignTypeIdList,
    setCurrency,
    setTrackEventFn,
    // UI状态
    nowStep,
    setNowStep,
    nowComponent,
    setNowComponent,

    editPresStep,
    selectedMethod,
    selectedPrescription,
    setSelectedMethod,
    setEditPresStep,
    setBtnDisable,
    setStepBtnText,
    // 重置store
    resetStore,
    setBrandExpand,
    // setPrescriptionType,
  } = useLensProcessStore();
  const prevEditPresStep = usePrevious(editPresStep);
  logger.log('clientPage reRender');

  // 设置埋点方法到store
  useEffect(() => {
    if (trackEvent) {
      setTrackEventFn(trackEvent);
    }
  }, [trackEvent]);

  const [allComponent, setAllComponent] = useState<string[]>([
    PRESCRIPTION_TYPE_COMPONENT,
    EDIT_PRESCRIPTION_COMPONENT,
    LENS_TYPE_COMPONENT,
    LENS_INDEX_COMPONENT,
  ]); // 所有组件

  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const currency = useStore((state) => state.currency);
  const visitorId = useStore((state) => state.visitorId);
  const setCannotSubmitData = useStore((state) => state.setCannotSubmitData);
  // 创建对EditPrescription组件的ref引用
  const editPrescriptionRef = useRef<EditPrescriptionRef>(null);
  const readersPrescriptionRef = useRef<ReadersPrescriptionRef>(null);

  const [lensTypeColorData, setLensTypeColorData] = useState<LensShowResponse>([]);
  const [lensIndexTip, setLensIndexTip] = useState<string>('');
  // 视野区域选择的类型
  const [selectedLensViewAreaType, setSelectedLensViewAreaType] = useState<ELensViewAreaType>(null);
  // 高级渐进镜视野范围数据
  const [progressiveViewArea, setProgressiveViewArea] =
    useState<GetResponseType<ApiResponseDefinition['GET:/api/lens/view_area']>['view_area']>(null);
  const [coatingList, setCoatingList] = useState<CoatingItem[]>([]);
  // const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  // const onCancelCorrectionDialog = () => {
  //   setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM);
  //   setShowCorrectionDialog(false);
  // };
  // const onContinueCorrectionDialog = async () => {
  //   const isSunglassesLens = glassPresTypeList?.some(
  //     (item) => item.title === EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION,
  //   );
  //   const prescriptionType = isSunglassesLens
  //     ? EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION
  //     : EPrescriptionType.NON_PRESCRIPTION;
  //   setPrescriptionType(prescriptionType);
  //   await getLensShow(prescriptionType);
  //   window.scrollTo({
  //     top: 0,
  //   });
  //   setShowCorrectionDialog(false);
  // };
  useEffectWithCurReady(() => {
    setIsRedeem(!!isRedeem);
    setCurrency(currency);

    let entrance_edit_lens_scene = '';
    let edit_lens_type = '';
    logger.warn('sessionStorage', sessionStorage.getItem('zl-sensors-lens-entrance-scene'));
    if (sessionStorage.getItem('zl-sensors-lens-entrance-scene')) {
      const zlSensorsLensEntranceScene = sessionStorage.getItem('zl-sensors-lens-entrance-scene');
      sessionStorage.removeItem('zl-sensors-lens-entrance-scene');
      const arr = zlSensorsLensEntranceScene.split('|');
      entrance_edit_lens_scene = arr[0] || '';
      edit_lens_type = arr[1] || '';
    }
    if (cartItemData) {
      setLensGoodsItem(cartItemData);
      const framePriceForSensor = handleGoodsFramePriceInfo(
        cartItemData.product,
        !!isRedeem,
        currency,
        userInfo,
      );
      setLensGoodsSensorsData({
        product_view_uuid: randomString() || '',
        shop_sku: cartItemData.product.shop_sku,
        business_sku: cartItemData.product.business_sku,
        product_sku: cartItemData.product.sku,
        product_id: cartItemData.product.id.toString(),
        product_name: cartItemData.product.short_name,
        entrance_edit_lens_scene,
        edit_lens_type,
        product_class_name: cartItemData.product.products_category,
        prescription_enter_type: null,
        frame_price: String(framePriceForSensor.price ?? ''),
        current_frame_price: String(framePriceForSensor.sell_price ?? ''),
        currency: currency?.name ?? '',
      });
    } else if (goodsDetail) {
      // 创建一个简化版的LensGoodsItem对象
      const innerLensGoodsDetail = {
        product_id: goodsDetail.product.id,
        product: {
          ...goodsDetail.product,
          // 确保exclusion字段符合要求
          exclusion: goodsDetail.product.exclusion
            ? JSON.stringify(goodsDetail.product.exclusion)
            : undefined,
          // 添加LensGoodsItem特有的字段
          price_info_pc_v0: goodsDetail.product.price_info_pc_v0,
          price_info_wap_v0: goodsDetail.product.price_info_wap_v0,
          price_info_pc_v1: goodsDetail.product.price_info_pc_v1,
          price_info_wap_v1: goodsDetail.product.price_info_wap_v1,
          price_info_pc_v2: goodsDetail.product.price_info_pc_v2,
          price_info_wap_v2: goodsDetail.product.price_info_wap_v2,
          price_info_pc_v3: goodsDetail.product.price_info_pc_v3,
          price_info_wap_v3: goodsDetail.product.price_info_wap_v3,
          img: goodsDetail.product.image?.product?.[0] || '',
          color: goodsDetail.product.product_frame_color_name,
        },
      }; // 使用类型断言
      const campaignIds = goodsDetail.product?.buried_point_data?.campaign_type_id_list || [];
      setCampaignTypeIdList(campaignIds);
      setLensGoodsItem(innerLensGoodsDetail);
      const framePriceForSensor = handleGoodsFramePriceInfo(
        innerLensGoodsDetail.product,
        !!isRedeem,
        currency,
        userInfo,
      );
      setLensGoodsSensorsData({
        product_view_uuid: randomString() || '',
        shop_sku: goodsDetail.product.shop_sku,
        business_sku: goodsDetail.product.business_sku,
        product_sku: goodsDetail.product.sku,
        product_id: goodsDetail.product.id.toString(),
        product_name: goodsDetail.product.short_name,
        entrance_edit_lens_scene,
        edit_lens_type,
        product_class_name: goodsDetail.product.products_category,
        prescription_enter_type: null,
        frame_price: String(framePriceForSensor.price ?? ''),
        current_frame_price: String(framePriceForSensor.sell_price ?? ''),
        currency: currency?.name ?? '',
      });
    }
    getCoatingList();

    // 埋点
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // 延迟上报，避免异步的 setState 还没生效
    setTimeout(() => {
      SDEditLensPageView();
    }, 200);
    return () => {
      // 组件卸载时执行的内容
      try {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        SDEditLensPageClose();
        resetStore();
      } catch (error) {
        logger.error('Failed to reset resetStore:', error);
      }
    };
  }, []);
  useEffect(() => {
    fetchPrescriptionMethodsData();
  }, [token]);
  useEffect(() => {
    if (
      editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS &&
      prevEditPresStep > EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS
    ) {
      fetchPrescriptionMethodsData();
    }
  }, [editPresStep]);
  useEffect(() => {
    setHasIndexPrice(isFrameProcess || isNonPrescriptionType);
  }, [isFrameProcess, isNonPrescriptionType]);

  useEffect(() => {
    if (lensGoodsItem) {
      if (userInfo?.id) {
        setTtqEventId(`${userInfo.id}${lensGoodsItem?.product?.shop_sku}`);
      } else {
        setTtqEventId(`${visitorId}${lensGoodsItem?.product?.shop_sku}`);
      }
    }
  }, [userInfo, lensGoodsItem, visitorId]);

  useEffect(() => {
    let newAllComponent = [];
    if (
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        prescriptionType as EPrescriptionType,
      )
    ) {
      newAllComponent = [PRESCRIPTION_TYPE_COMPONENT, LENS_TYPE_COMPONENT];
    } else if ([EPrescriptionType.READERS].includes(prescriptionType as EPrescriptionType)) {
      if (isWap) {
        newAllComponent = [PRESCRIPTION_TYPE_COMPONENT, LENS_TYPE_COMPONENT, LENS_INDEX_COMPONENT];
      } else {
        newAllComponent = [
          PRESCRIPTION_TYPE_COMPONENT,
          EDIT_PRESCRIPTION_COMPONENT,
          LENS_TYPE_COMPONENT,
          LENS_INDEX_COMPONENT,
        ];
      }
    } else if (
      prescriptionType === EPrescriptionType.PROGRESSIVE &&
      progressiveViewArea &&
      selectedLensIndexItem?.advanced
    ) {
      newAllComponent = [
        PRESCRIPTION_TYPE_COMPONENT,
        EDIT_PRESCRIPTION_COMPONENT,
        LENS_TYPE_COMPONENT,
        LENS_INDEX_COMPONENT,
        LENS_VIEW_AREA_COMPONENT,
      ];
    } else {
      newAllComponent = [
        PRESCRIPTION_TYPE_COMPONENT,
        EDIT_PRESCRIPTION_COMPONENT,
        LENS_TYPE_COMPONENT,
        LENS_INDEX_COMPONENT,
      ];
    }
    if (
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        prescriptionType as EPrescriptionType,
      ) &&
      (!selectedColorItem || selectedColorItem?.is_coating === 1)
    ) {
      newAllComponent.push(COATING_COMPONENT);
    }
    setAllComponent(newAllComponent);
    setNowComponent(newAllComponent[nowStep]);

    setIsNonPrescriptionType(
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        prescriptionType as EPrescriptionType,
      ),
    );
  }, [prescriptionType, progressiveViewArea, nowStep, selectedLensIndexItem, selectedColorItem]);

  useEffect(() => {
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      setStepBtnText(addToCartText);
    } else {
      setStepBtnText(t('next'));
    }
    switch (nowComponent) {
      case EDIT_PRESCRIPTION_COMPONENT: {
        if (prescriptionType === EPrescriptionType.READERS && !isWap) {
          setBtnDisable(readerSelectedIndex < 0);
          setStepBtnText(t('next'));
          return;
        }
        setBtnDisable(false);
        // 只有在填写处方表单步骤时才显示next按钮，否则为空串
        const { FILL_PRESCRIPTION_FORM, CHOOSE_PRESCRIPTION } = EDIT_PRESCRIPTION_SUB_STEPS;
        if (CHOOSE_PRESCRIPTION === editPresStep) {
          setStepBtnText(t('next'));
        } else if (FILL_PRESCRIPTION_FORM === editPresStep) {
          const isShowSubmit = prevEditPresStep === CHOOSE_PRESCRIPTION;
          setStepBtnText(isShowSubmit ? t('submit') : t('next'));
        } else {
          setStepBtnText('');
        }
        return;
      }
      case LENS_TYPE_COMPONENT:
        setBtnDisable(!selectedColorItem?.lens_color);
        break;
      case LENS_INDEX_COMPONENT:
        setBtnDisable(!selectedLensIndexItem?.index_name);
        break;
      case LENS_VIEW_AREA_COMPONENT:
        setBtnDisable(!selectedLensViewAreaType);
        break;
      case COATING_COMPONENT:
        setBtnDisable(!selectedCoating);
        break;
      default:
        setBtnDisable(true);
    }
  }, [
    allComponent,
    nowStep,
    nowComponent,
    editPresStep,
    selectedColorItem,
    selectedLensIndexItem,
    selectedLensViewAreaType,
    selectedCoating,
    readerSelectedIndex,
  ]);

  useEffect(() => {
    // 初始化商品信息
    if (lensGoodsItem && lensGoodsItem.product) {
      // 设置商品图片 注意购物车、详情页、积分兑换不同取值逻辑不同
      // 这里可以添加价格计算逻辑
      const innerGoodsFramePriceInfo = handleGoodsFramePriceInfo(
        lensGoodsItem.product,
        isRedeem,
        currency,
        userInfo,
      );
      let innerLensTypePriceInfo: IPrice;
      let innerLensIndexPriceInfo: IPrice;
      let innerCoatingPriceInfo: IPrice;
      if (selectedAdvancedLensIndexItem) {
        innerLensTypePriceInfo = handleColorItemPriceInfo(
          selectedAdvancedLensIndexItem,
          false,
          currency,
          userInfo,
        );
        innerLensIndexPriceInfo = handleLensIndexPriceInfo(
          selectedAdvancedLensIndexItem,
          currency,
          userInfo,
        );
      } else {
        if (selectedColorItem) {
          innerLensTypePriceInfo = handleColorItemPriceInfo(
            selectedColorItem,
            hasIndexPrice,
            currency,
            userInfo,
          );
        }
        if (selectedLensIndexItem) {
          innerLensIndexPriceInfo = handleLensIndexPriceInfo(
            selectedLensIndexItem,
            currency,
            userInfo,
          );
        }
      }
      if (selectedCoating) {
        innerCoatingPriceInfo = handleCoatingPriceInfo(selectedCoating, currency, userInfo);
      }
      setGoodsFramePriceInfo(innerGoodsFramePriceInfo);
      setLensTypePriceInfo(innerLensTypePriceInfo);
      setLensIndexPriceInfo(innerLensIndexPriceInfo);
      setCoatingPriceInfo(innerCoatingPriceInfo);
      setTotalPrice({
        currency: currency.name,
        sell_price: add(
          innerGoodsFramePriceInfo.sell_price,
          innerLensTypePriceInfo?.sell_price || 0,
          innerLensIndexPriceInfo?.sell_price || 0,
          innerCoatingPriceInfo?.sell_price || 0,
        ).toFixed(2),
        price: add(
          innerGoodsFramePriceInfo.price,
          innerLensTypePriceInfo?.price || 0,
          innerLensIndexPriceInfo?.price || 0,
          innerCoatingPriceInfo?.price || 0,
        ).toFixed(2),
      });
    }
  }, [
    lensGoodsItem,
    userInfo,
    currency,
    isRedeem,
    hasIndexPrice,
    selectedColorItem,
    selectedLensIndexItem,
    selectedAdvancedLensIndexItem,
    selectedCoating,
  ]);

  useEffect(() => {
    if (prescriptionType && glassPresTypeList) {
      glassPresTypeList?.forEach((item) => {
        if (item.title === prescriptionType) {
          setPresTypeName(item.name);
          setPresTypeOptionName(item.name);
        } else if (item.items?.length) {
          item.items?.forEach((child) => {
            if (child.title === prescriptionType) {
              // 展示层总是取一级名称
              setPresTypeName(item.name);
              // 神策埋点取子选项名称
              setPresTypeOptionName(child.name);
            }
          });
        }
      });
    } else {
      setPresTypeName('');
      setPresTypeOptionName('');
    }
  }, [prescriptionType, glassPresTypeList]);

  const handleClose = () => {
    if (lensGoodsItem.cart_id) {
      onClose();
    } else {
      router.back();
    }

    SDSendLeaveEvent('Close');
  };
  const handleComplete = (data: any) => {
    if (lensGoodsItem.cart_id) {
      onClose();
    } else {
      const routePath =
        isRedeem && data?.cart_ids?.[0] ? `/payment?redeemCartId=${data.cart_ids[0]}` : '/cart';
      router.replace(routePath);
    }
  };
  const handleDoUpdateCart = (data: IBaseCartData) => {
    onUpdateCart?.(data);
    handleComplete(data);
  };

  const handlePrescriptionTypeNextStep = async (
    type: EPrescriptionType,
    presValue?: IPrescriptionOptions,
  ) => {
    if (
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        type,
      ) ||
      (type === EPrescriptionType.READERS && isWap)
    ) {
      await getLensShow(type, presValue);
      setNowStep(nowStep + 1);
    } else {
      setNowStep(nowStep + 1);
    }
    scrollToTop();
  };

  const handleSubmitPrescription = async (
    presValue: IPrescriptionOptions,
    pres?: CustomerPrescription,
  ) => {
    setPrescription(presValue);
    initLensType();
    // 使用传入的最新处方数据调用 getLensShow，而不是依赖状态中的处方数据
    await getLensShow(prescriptionType, presValue, pres);
    if (prescriptionType === EPrescriptionType.PROGRESSIVE) {
      getProgressiveViewArea();
    }
    setNowStep(nowStep + 1);
    scrollToTop();
  };

  // 处理返回按钮点击
  const handleLeftBack = () => {
    if (nowStep <= 0) return;

    // app 这种情况不会报这个事件
    // 注意：上报埋点放在前面，避免被离开的步骤状态销毁而拿不到数据。
    SDSendLeaveEvent('Return');
    const { SELECT_METHODS, CHOOSE_PRESCRIPTION, FILL_PRESCRIPTION_FORM } =
      EDIT_PRESCRIPTION_SUB_STEPS;
    switch (nowComponent) {
      // 如果处于编辑处方流程且不处于选择处方填写方式步骤
      case EDIT_PRESCRIPTION_COMPONENT: {
        const targetStep = calculatePrevStep();
        if (editPresStep !== SELECT_METHODS) {
          return setEditPresStep(targetStep);
        }

        break;
      }
      case LENS_TYPE_COMPONENT:
        if (selectedMethod?.type === CHOOSE_PRESCRIPTION && editPresStep === CHOOSE_PRESCRIPTION) {
          setNowStep(nowStep - 1);
          initComponent(allComponent[nowStep - 1]);
          return setEditPresStep(CHOOSE_PRESCRIPTION);
        } else {
          // 否则默认返回填写处方步骤
          setEditPresStep(FILL_PRESCRIPTION_FORM);
        }
        break;
    }
    setNowStep(nowStep - 1);
    initComponent(allComponent[nowStep - 1]);
  };
  const calculatePrevStep = () => {
    const { type = 0 } = selectedMethod || {};
    const { FILL_PRESCRIPTION_FORM } = EDIT_PRESCRIPTION_SUB_STEPS;
    // * 选择了填写处方，
    if (type === FILL_PRESCRIPTION_FORM) {
      return 0;
    }
    // * 扫描处方 /选择处方 可能还没到达填写处方步骤
    return type === editPresStep ? 0 : type;
  };
  // 各步骤的初始化逻辑
  const initLensIndex = () => {
    setSelectedLensIndexItem(null);
  };
  const initLensType = () => {
    setSelectedLensType(null);
    setSelectedBrand(null);
    setSelectedColorItem(null);
    setBrandExpand({});
  };
  const initEditPrescription = () => {
    setPrescription(getEmptyPrescription());
    // 与 resetStore 一致：清空处方时一并清空「选择的填写方式」和子步骤，避免 selectedMethod=null 但 editPresStep 仍为 CHOOSE_PRESCRIPTION/FILL 导致 UI 与状态不一致
    setSelectedMethod(null);
    setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS);
  };
  const initLensViewArea = () => {
    setSelectedLensViewAreaType(null);
    setSelectedAdvancedLensIndexItem(null);
  };
  const initCoating = () => {
    setSelectedCoating(null);
  };

  const initMap = {
    [LENS_TYPE_COMPONENT]: initLensType,
    [EDIT_PRESCRIPTION_COMPONENT]: initEditPrescription,
    [LENS_INDEX_COMPONENT]: initLensIndex,
    [LENS_VIEW_AREA_COMPONENT]: initLensViewArea,
    [COATING_COMPONENT]: initCoating,
  };

  const initComponent = (component: string) => {
    const idx = allComponent.indexOf(component);
    const afterComponents = allComponent.slice(idx + 1);
    afterComponents.forEach((com) => {
      initMap[com]?.();
    });
    if (component === PRESCRIPTION_TYPE_COMPONENT) {
      initEditPrescription();
    }
  };

  const scrollToTop = () => {
    if (isWap) {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    } else {
      const rightPart = document.querySelector('#right-part');
      if (rightPart) {
        rightPart.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
    }
  };

  // 处理下一步按钮点击
  const handleNextStep = async () => {
    logger.warn('handleNextStep===>', nowComponent);
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      handleAddToCart();
      return;
    }
    // initComponent(nowComponent);
    switch (nowComponent) {
      case EDIT_PRESCRIPTION_COMPONENT: {
        const { CHOOSE_PRESCRIPTION } = EDIT_PRESCRIPTION_SUB_STEPS;
        // 如果当前是选择处方步骤，则调用选择处方方法
        if (editPresStep === CHOOSE_PRESCRIPTION) {
          editPrescriptionRef.current?.confirmChosenPrescription();
          return;
        }
        if (prescriptionType === EPrescriptionType.READERS && !isWap) {
          if (readersPrescriptionRef.current) {
            readersPrescriptionRef.current.handleConfirm();
            return;
          }
        } else if (editPrescriptionRef.current) {
          editPrescriptionRef.current.confirmPrescription();
          // 这里不走下一步.return 掉，避免走后面的置顶逻辑
          return;
        }
        break;
      }
      case LENS_TYPE_COMPONENT:
        if (selectedLensType && selectedColorItem) {
          // 下一步可能是折射率或者镀膜
          const lensFromColor = selectedColorItem?.lens_list?.[0];
          const apiPres = getApiPrescriptionType(prescriptionType);
          const shouldReportLensSkuOnNext =
            apiPres === EPrescriptionType.NON_PRESCRIPTION ||
            apiPres === EPrescriptionType.FRAME_ONLY;
          SDLensTypePageNextClick(
            shouldReportLensSkuOnNext
              ? {
                  lens_id: lensFromColor?.id != null ? `${lensFromColor.id}` : '',
                  lens_code: lensFromColor?.code ?? '',
                }
              : {},
          );
          setNowStep(nowStep + 1);
        }
        break;
      case LENS_INDEX_COMPONENT:
        if (selectedLensIndexItem) {
          // 下一步可能是视野区域或者镀膜
          if (nextComponent === LENS_VIEW_AREA_COMPONENT) {
            // initLensViewArea();
          } else if (nextComponent === COATING_COMPONENT) {
            // initCoating();
          }
          setNowStep(nowStep + 1);

          SDLensIndexPageNextClick();
        }
        break;
      case LENS_VIEW_AREA_COMPONENT:
        // 是否走到镀膜步骤由魔方配置决定。能走到这里，说明有下一步
        setNowStep(nowStep + 1);
        break;
      case COATING_COMPONENT:
        // 这里没有下一步 直接加购
        handleAddToCart();
        break;
      default:
        break;
    }
    scrollToTop();
  };

  // 调用镜片类型接口
  const getLensShow = async (
    presType: EPrescriptionType | '' | undefined,
    currentPrescription?: IPrescriptionOptions,
    selectedPres?: CustomerPrescription,
  ) => {
    try {
      setIsGlobalLoading(true);
      // 使用传入的处方数据或者状态中的处方数据
      const prescriptionData = currentPrescription || prescription || getEmptyPrescription();
      let prescription_type = presType || prescriptionType;
      if (
        [EPrescriptionType.READERS, EPrescriptionType.PRESCRIPTION_READING].includes(
          prescription_type,
        )
      ) {
        prescription_type = EPrescriptionType.READING_GLASSES;
      }

      const is_prism = hasPrism(prescriptionData) ? '1' : '2';

      const { data, code } = await fetchLensShow({
        prescription_type,
        product_id: lensGoodsItem.product_id.toString(),
        od_sph: prescriptionData.od_sph,
        os_sph: prescriptionData.os_sph,
        od_cyl: prescriptionData.od_cyl,
        os_cyl: prescriptionData.os_cyl,
        os_add: prescriptionData.os_add,
        od_add: prescriptionData.od_add,
        is_prism,
        prescription_id: selectedPres?.id,
      });
      if (code === 200 && Array.isArray(data)) {
        const lensData = data as LensShowResponse;
        lensData.forEach((lensType) => {
          if (lensType.merge_brand === 1) {
            lensType.brand_list?.forEach((brand) => {
              if (brand.color_list?.length) {
                brand.default_lens_color = brand.color_list[0].lens_color;
              }
            });
          } else if (lensType.color_list?.length) {
            lensType.default_lens_color = lensType.color_list[0].lens_color;
          }
        });
        setLensTypeColorData(lensData);
        setLensIndexTip(lensData[0]?.index_tip || '');
      } else {
        setLensIndexTip('');
        setLensTypeColorData([]);
      }
    } catch (error) {
      logger.error('👀', error);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const getProgressiveViewArea = async () => {
    const { data, code } = await fetchProgressiveViewArea();
    if (code === 200) {
      setProgressiveViewArea(data.view_area);
    } else {
      setProgressiveViewArea(null);
    }
  };

  const getCoatingList = async () => {
    const { data, code } = await fetchCoatingsShow();
    if (code === 200) {
      setCoatingList(data);
    } else {
      setCoatingList([]);
    }
  };
  const fetchPrescriptionMethodsData = async () => {
    try {
      setIsGlobalLoading(true);
      const { data, code } = await fetchPrescriptionFillMethods();

      if (code === 200) {
        const { lens_type, prescription_type_count } = data;
        setPrescriptionFillMethodsData({
          methods: lens_type,
          prescriptionTypesCount: prescription_type_count,
        });
      }
    } catch (error) {
      logger.error('get prescription fill methods data error', error);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  /**
   * @description: 加入购物车数据处理
   * @return {*}
   */
  const handleAddToCart = async () => {
    setIsGlobalLoading(true);
    const addToCartParams = {
      ...prescription,
      prescription_type: getApiPrescriptionType(prescriptionType),
      lens_id: selectedAdvancedLensIndexItem?.id || selectedLensIndexItem?.id || 0,
      coating_id: selectedCoating?.id || 0,
    };
    delete addToCartParams.is_prism;
    delete addToCartParams.is_two_pd;
    if (
      [EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION, EPrescriptionType.NON_PRESCRIPTION].includes(
        prescriptionType,
      )
    ) {
      addToCartParams.lens_id = selectedColorItem?.lens_list?.[0]?.id || 0;
    }
    // 跟之前的线上逻辑保持一致：处方类型是太阳镜、非处方、镜框、成品老化，则把0.00和None改为空字符串。
    if (
      [
        EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION,
        EPrescriptionType.NON_PRESCRIPTION,
        EPrescriptionType.FRAME_ONLY,
        EPrescriptionType.READERS,
      ].includes(prescriptionType)
    ) {
      Object.keys(addToCartParams).forEach((key) => {
        if (['0.00', 'None'].includes(addToCartParams[key])) {
          addToCartParams[key] = '';
        }
      });
    }
    const addToCartReqParams = {
      currency: currency.name,
      product_id: lensGoodsItem.product_id,
      qty: 1,
      conversion_qty: 1,
      option: addToCartParams,
      prescription_recognition_id: scanInfo?.recordId,
      prescriptions_image: scanInfo?.imageUrl || selectedPrescription?.prescriptions_image || '',
      origin_option: getFeatureValueUsedList(lensGoodsItem.product.shop_sku),
    };

    if (isRedeem) {
      await handleRedeemAddToCart(addToCartReqParams);
      setScanInfo(null);
      return;
    }

    SDAddToCartBtnClick();
    if (cartItemData?.cart_id) {
      await handleUpdateCart(addToCartReqParams);
    } else {
      await addToCartFn(addToCartReqParams);
    }
    setScanInfo(null);
    // this.SET_PREVIOUS_PRESCRIPTION_TYPE('')
  };

  const handleSendPoints = (requestData: BaseCartRequestData) => {
    try {
      sendPoints(requestData);
    } catch (e) {
      logger.error('👀', e);
    }
  };
  /**
   * @description: 积分商城，加购完直接进入支付页
   * @param {*} params
   * @return {*}
   */
  const handleRedeemAddToCart = async (params: BaseCartRequestData) => {
    try {
      const finalParams = handlerPrescriptionData(params);
      const paymentParams = {
        currency: currency.name,
        data: [
          {
            type: 6,
            relation_id: 0,
            products: [finalParams],
          },
        ],
      };
      const { code, data, msg } = await specialCartStore(paymentParams);
      if (code === 200) {
        sessionStorage.setItem('zl-sensors-entrance-payment-scene', '积分商城详情页兑换');
        router.push(`/payment?redeemCartId=${data.cart_ids[0]}`);
      } else if (code === 400880) {
        router.back();
        // 展示限购弹窗
        setCannotSubmitData({
          ...data,
          show: true,
          onClose: () => {
            router.back();
          },
        });
      } else {
        toast.error(msg);
      }
    } catch (error) {
      logger.error('👀', error);
    } finally {
      setIsGlobalLoading(false);
    }
  };
  /**
   * @description: 加入购物车
   * @param {*} params
   * @return {*}
   */
  const addToCartFn = async (params: BaseCartRequestData) => {
    const finalParams = handlerPrescriptionData(params);
    try {
      const res = await addToCart(finalParams);
      if (res.code === 200) {
        try {
          handleSendPoints(finalParams);
          SDAddToCartResult({
            is_success: true,
            service_code: '200',
            msg: res.msg,
          });
        } catch (e) {
          logger.error('👀', e);
        }
        // TODO 这里需要优化。

        sessionStorage.setItem('zl-sensors-entrance-cart-scene', '配镜流程加购');
        handleComplete(res.data);
        // 跳转到购物车带特殊标记，用于app引流
        sessionStorage.setItem('toApp', '1');
      } else {
        SDAddToCartResult({
          is_success: false,
          service_code: `${res.code}`,
          msg: res.msg,
        });
      }
    } catch (err) {
      logger.error('👀', err);
      SDAddToCartResult({
        is_success: false,
        service_code: `${err.code}`,
        msg: err.msg,
      });
    } finally {
      setIsGlobalLoading(false);
    }
  };
  /**
   * @description: 从购物车到处方进行更新购物车
   * @return {*}
   */
  const handleUpdateCart = async (params: BaseCartRequestData) => {
    const finalParams = handlerPrescriptionData({
      ...params,
      cart_id: lensGoodsItem.cart_id,
      qty: lensGoodsItem?.qty || 1,
    });
    if (!params?.prescription_recognition_id) {
      finalParams.prescription_recognition_id = 0;
      // * 如果是选择我的处方，是有可能没有识别处方id，但是有图片的
      // finalParams.prescriptions_image = '';
    }
    const { code, data, msg } = await updateCart(finalParams);
    logger.error('👀', code, data, msg);
    if (code === 200) {
      SDAddToCartResult({
        is_success: true,
        service_code: '200',
        msg,
      });
    } else {
      SDAddToCartResult({
        is_success: false,
        service_code: `${code}`,
        msg,
      });
    }
    // 重新获取购物车列表 411找不到游客的cart_id或购物车不属于当前人
    // if (code === 200 || code === 411) {
    //   // this.$emit('reloadCart', [], data);
    //   // this.close();
    // }
    setIsGlobalLoading(false);
    handleDoUpdateCart(data);
  };

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      SDSendLeaveEvent('Else');
    }
  }

  const [minHeight, setMinHeight] = useState(0);

  // TODO 开发环境，在真机上，会出现，刚开始body仍然可以滚动的情况（时间大约5s）
  if (inDialog) {
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
  }

  useEffect(() => {
    // 设置背景色 （页面的最小高度不好控制）
    document.body.style.backgroundColor = '#f4f4f4';
    if (inDialog) {
      document.documentElement.style.height = '100%';
      document.body.style.height = '100%';
      document.body.style.overflow = 'hidden';
    }
    // 解决谷歌徽章遮挡问题
    if (window.___ratingbadge_0?.style) {
      window.___ratingbadge_0.style.opacity = '0';
    }
    setMinHeight(window.innerHeight);

    return () => {
      document.documentElement.style.height = 'auto';
      document.body.style.height = 'auto';
      document.body.style.overflow = 'auto';
      document.body.style.backgroundColor = '';
      if (window.___ratingbadge_0?.style) {
        window.___ratingbadge_0.style.opacity = '1';
      }
    };
  }, []);
  return (
    // 在dialog中，由于从 html到这里都设置了 height: 100% ，所以 height:100% 可以生效。
    // 在页面里面不会生效
    <article
      className={`${processClassName.article} ${inDialog ? 'max-xl:min-h-full' : ''} ${editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.PRESCRIPTION_RECOGNITION ? '!pb-[70px]' : ''}`}
      style={{ minHeight: `${minHeight}px` }}
    >
      <ClientLeft isFrameProcess={isFrameProcess} />
      <section
        id="right-part"
        className={`right-part ${processClassName.rightPart} h-full overflow-y-auto box-border`}
      >
        <div className={processClassName.rightPartContent}>
          <div
            className={`right-box ${processClassName.rightPartBox} xl:pt-[20px] xl:pb-[20px]`}
            id="right-box-content"
          >
            {/* 底部区域 */}
            <Footer isFrameProcess={isFrameProcess} onNextStep={handleNextStep} />
            {/* 顶部区域 */}
            <Header
              isFrameProcess={isFrameProcess}
              onLeftBack={handleLeftBack}
              onClose={handleClose}
              inDialog={inDialog}
            />

            {/* 中间区域 */}
            {nowComponent === PRESCRIPTION_TYPE_COMPONENT && (
              <PrescriptionType
                presList={glassPresTypeList}
                toNextStep={handlePrescriptionTypeNextStep}
              />
            )}
            {nowComponent === EDIT_PRESCRIPTION_COMPONENT &&
              (prescriptionType === EPrescriptionType.READERS && !isWap ? (
                <ReadersPrescription
                  ref={readersPrescriptionRef}
                  onConfirm={handleSubmitPrescription}
                />
              ) : (
                <EditPrescription
                  ref={editPrescriptionRef}
                  prescriptionType={prescriptionType}
                  onSubmitPrescription={handleSubmitPrescription}
                />
              ))}
            {nowComponent === LENS_TYPE_COMPONENT && (
              <LensType
                isFrameProcess={isFrameProcess}
                lensTypeColorData={lensTypeColorData}
                selectedLensType={selectedLensType}
                selectedBrand={selectedBrand}
                selectedColorItem={selectedColorItem}
                onSelectedLensType={setSelectedLensType}
                onSelectedBrand={setSelectedBrand}
                onSelectedColorItem={setSelectedColorItem}
                onBackToEditPrescription={handleLeftBack}
              />
            )}
            {nowComponent === LENS_INDEX_COMPONENT && (
              <LensIndex
                lensIndexTip={lensIndexTip}
                selectedLensIndexItem={selectedLensIndexItem}
                onSelectedLensIndexItem={setSelectedLensIndexItem}
              />
            )}
            {nowComponent === LENS_VIEW_AREA_COMPONENT && (
              <LensViewArea
                progressiveViewArea={progressiveViewArea}
                selectedLensIndexItem={selectedLensIndexItem}
                selectedLensViewAreaType={selectedLensViewAreaType}
                onSelectedLensViewAreaType={setSelectedLensViewAreaType}
                onSelectedAdvancedLensIndexItem={setSelectedAdvancedLensIndexItem}
              />
            )}
            {nowComponent === COATING_COMPONENT && (
              <Coating
                coatingList={coatingList}
                selectedCoating={selectedCoating}
                onSelectedCoating={setSelectedCoating}
              />
            )}
          </div>
          <div className={`right-side ${processClassName.rightPartSide}`} />
        </div>
      </section>

      {/* 处方纠错弹窗忽然产品不想要了，这里暂时保留，先不删除 */}
      {/* <PrescriptionCorrectionDialog
        open={showCorrectionDialog}
        onCancel={onCancelCorrectionDialog}
        onContinue={onContinueCorrectionDialog}
      /> */}
    </article>
  );
}
